import { getDefaultStore } from 'jotai';
import * as Sentry from '@sentry/react';
import * as native from '@/utils/native';
import { isTeamSubscription, PurchaseScene, purchaseScene } from '@/constants';
import { fetchMemberInfo, fetchTeamInfo, fetchUserInfo, fetchZhankuManager } from '@/services/user';
import {
  accountUrlAtom,
  batchSizeAtom,
  curProductAtom,
  cutoutInfoAtom,
  dayNumListAtom,
  hasFetchUserInfoAtom,
  identificationAtom,
  isFromZhankuAtom,
  meidouForeverTotalAtom,
  meidouTotalAtom,
  teamInfoAtom,
  teamRoleAtom,
  userInfoAtom,
  vipInfoAtom,
  ZhankuManagerInfoAtom,
} from '@/store';
import { closeVipSubscribeDialog, getIncomeSource } from '@/utils/vip';
import { getUrlParam, showToast } from '.';
import {
  coinDetail,
  fetchAccountLoginUrl,
  fetchAvailableThirdParty,
  fetchUserBatchSize,
} from '@/services/subscription';
import { compareVersions } from 'compare-versions';
import { track } from './common';

const store = getDefaultStore();

/**
 * 获取相关用户信息
 */
export const getUserInfo = async () => {
  try {
    // const res = await Promise.all([fetchUserInfo(), getUserInfoFromPlatform()]);
    const requestList: any[] = [fetchUserInfo()];
    if (isTeamSubscription) {
      requestList.push(fetchTeamInfo());
      requestList.push(fetchMemberInfo());
    }
    const res = await Promise.all(requestList);
    const data = res[0]?.data || {};
    store.set(hasFetchUserInfoAtom, true);
    let access_token = '';
    let uid = null;
    if (data.code === 0 && data.data?.userInfo?.id) {
      store.set(userInfoAtom, data.data.userInfo);
      store.set(identificationAtom, data.data.identification);
      store.set(isFromZhankuAtom, data.data.is_from_zhanku);
      store.set(cutoutInfoAtom, data.data.cutoutInfo);
      store.set(vipInfoAtom, data.data.vipInfo);
      store.set(meidouTotalAtom, data.data.coinInfo.total_amount); // 没有余额从这个接口更新，用于埋点上报residual_meidou参数的值
      uid = data.data.userInfo.id;
      // window.localStorage.setItem('uid', uid);
      if (uid) {
        Sentry.setUser({
          id: `${uid}`,
        });
      }

      access_token = data.data.access_token;

      if (data.data.is_from_zhanku) getZhankuManager();
    }

    if (res[1]?.data?.code === 0 && res[1]?.data?.data) {
      const teamData = res[1]?.data.data || {};
      store.set(teamInfoAtom, teamData || {});

      // 团队用户处于后台赠送时长时，不允许加购，自动关闭弹窗
      if (
        [2, 8].includes(teamData?.active_order?.ref_provider) &&
        purchaseScene === PurchaseScene.EXTRA
      ) {
        showToast('非正常订阅的团队会员不支持加购席位');
        setTimeout(() => {
          closeVipSubscribeDialog();
        }, 3000);
      }
    }

    if (res[2]?.data?.code === 0 && res[2]?.data?.data) {
      store.set(teamRoleAtom, res[2]?.data?.data?.role);
    }

    return { access_token, uid };
  } catch (e) {
    store.set(hasFetchUserInfoAtom, true);
    return { access_token: '', uid: null };
  }
};

/**
 * 获取海洛顾问信息
 */
export const getZhankuManager = async () => {
  const res = await fetchZhankuManager();
  if (res?.data.code === 0) {
    store.set(ZhankuManagerInfoAtom, res?.data.data || {});
  }
};

/**
 * 获取批处理剩余张数
 */
export const getUserBatchSize = async () => {
  const res = await fetchUserBatchSize();
  if (res?.status === 200) {
    store.set(batchSizeAtom, res?.data?.batch_size || 0);
  }
};

// 美豆余量
export const getMeidouCount = async () => {
  const res = await coinDetail();
  if (res?.data?.data?.day_num_list) {
    store.set(dayNumListAtom, res.data.data.day_num_list);
  }
  if (res?.data?.data?.total_forever_amount) {
    store.set(meidouForeverTotalAtom, res.data.data.total_forever_amount);
  }
  if (res?.data?.data?.total_amount) {
    store.set(meidouTotalAtom, res.data.data.total_amount);
  }
};

/**
 * 获取PC端的登录跳转地址
 */
export const getAccountLoginUrl = async (type: string) => {
  const entrance = getUrlParam('entrance') || '';
  const channel = getUrlParam('channel') || '';
  const income_source = getUrlParam('income_source') || '';
  const theme = getUrlParam('theme') || '';
  const gid = getUrlParam('Gid') || '';

  const [clientId, version] = await Promise.all([native.getClientId(), native.getVersion()]);

  const availableRes = await fetchAvailableThirdParty({
    client_id: clientId,
    version,
    channel,
  });

  // 是否开启历史登录
  let enableLoginHistory = false;
  // compareVersions: 相等时返回0，小于时返回1，大于时返回-1
  if (
    (version && getIncomeSource() === 'MTXX-PC' && compareVersions('7.0.8.5', version) <= 0) ||
    (version && getIncomeSource() === 'MTXX-MAC' && compareVersions('1.7.50', version) <= 0)
  ) {
    // windows 7.0.8.0版本开始支持
    // mac 1.7.50版本开始支持
    enableLoginHistory = true;
  }

  let res;
  const queryParams: any = {
    type,
    income_source,
    channel,
    entrance,
    theme,
    Gid: gid,
  };

  let queryStr = '';
  for (const key in queryParams) {
    if (key) {
      queryStr = queryStr + `${key}=${queryParams[key]}&`;
    }
  }
  // 剔除最后一个&字符
  queryStr = queryStr.replace(/&$/, '');

  if (enableLoginHistory) {
    let prefix = process.env.REACT_APP_ENV;

    if (process.env.REACT_APP_ENV === 'release') {
      prefix = '';
    }
    res = await fetchAccountLoginUrl({
      callback_url: encodeURIComponent(
        `${window.location.origin}/callback.html?version=${version || Math.random()}&${queryStr}`
      ),
    });
  } else {
    res = await fetchAccountLoginUrl({
      callback_url: `${window.location.origin}${window.location.pathname}?${queryStr}`,
    });
  }

  if (res && res?.data?.redirect_url) {
    let url = res.data.redirect_url || '';

    if (url.indexOf('state=') > -1) {
      url = url.replace(/state=(\d+)/, 'state=sub');
    } else if (url.indexOf('client_referer=') > -1) {
      url = url.replace(/client_referer=(\d+)/, 'client_referer=sub');
    }
    if (availableRes && availableRes?.data?.data?.available.toString()) {
      url += `&show_third_party_accounts=${availableRes.data.data.available === 1 ? true : false}`;
    }

    url += '&copyright=hide';
    // 是否开启历史登录
    if (enableLoginHistory) {
      url = url.replace('redirect_callback', 'client_callback');

      url += `&client_configs=${JSON.stringify({
        enable_login_history: 1,
      })}`;

      // 回跳
      queryParams.redirect_url = `${window.location.origin}${window.location.pathname}`;
      queryParams.client_referer = 'sub';
      queryParams.state = 'sub';
      // 透传回调地址的query参数
      url += `&client_params=${JSON.stringify(queryParams)}`;
    }

    store.set(accountUrlAtom, url);
  }
};

/**
 * PC内嵌登录回跳逻辑
 */
export const handleLogin = () => {
  if (getIncomeSource() !== 'MTXX-WEB') {
    window.location.href = store.get(accountUrlAtom);
  }
};

/**
 * 点击登录处理函数
 */
export const handleJumpLogin = () => {
  if (getIncomeSource() !== 'MTXX-WEB') {
    const curProduct = store.get(curProductAtom);
    track({
      event_id: 'vip_page_click',
      event_name: '会员落地页点击',
      params: {
        category: '点击登录',
        product_id: curProduct?.productId || '',
        country_code: curProduct?.countryCode || '',
      },
    });
  }
};
